import React, {useEffect, useState} from 'react';
import {SearchIcon, XIcon} from './icons';
import createAxiosInstance from "../support/AxiosSupport";
import {formatDateTime, toDateTimeLocalValue} from '../utils/dateTime';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import PageSizeSelect from './PageSizeSelect';
import {usePageSize} from '../hooks/usePageSize';

interface AuditMessage {
    id: string;
    timestamp: string;
    username: string;
    userId: string;
    action: string;
    ipAddress: string;
    userAgent: string;
    success: boolean;
    parameters: string;
}

const Audit: React.FC = () => {
    const [auditMessages, setAuditMessages] = useState<AuditMessage[]>([]);
    const [page, setPage] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    // Размер страницы журнала сохраняется между сессиями и своим ключом (CTHS-7): смена
    // размера возвращает на первую страницу, иначе администратор оказывался на странице,
    // которой после пересчёта уже нет.
    const [batchSize, setBatchSize] = usePageSize('audit', {onChange: () => setPage(1)});
    const [query, setQuery] = useState('');
    const [status, setStatus] = useState<boolean | undefined>(undefined);
    const [username, setUsername] = useState('');
    const [userId, setUserId] = useState('');
    const [ip, setIp] = useState('');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [opened, setOpened] = useState(false);
    const [selected, setSelected] = useState<AuditMessage | null>(null);

    useDocumentTitle('Журнал аудита');

    useEffect(() => {
        async function fetchData() {
            const axiosInstance = await createAxiosInstance();
            const params = new URLSearchParams();
            params.append('page', String(page - 1));
            params.append('size', String(batchSize));
            if (query) params.append('action', query);
            if (username) params.append('username', username);
            if (userId) params.append('userId', userId);
            if (typeof status === 'boolean') params.append('success', String(status));
            if (ip) params.append('ip', ip);
            if (dateRange[0] && dateRange[1]) {
                params.append('from', String(dateRange[0].valueOf()));
                params.append('to', String(dateRange[1].valueOf()));
            }
            axiosInstance.get(`/audit/messages?${params.toString()}`)
                .then(response => {
                    setTotalElements(response.data.totalElements);
                    setAuditMessages(response.data.content);
                })
                .catch(error => console.error('Error fetching /audit/messages:', error));
        }

        fetchData();
    }, [page, batchSize, query, username, userId, ip, status, dateRange]);

    const handleSearch = () => {
        setPage(1);
    };

    const clearFilters = () => {
        setQuery('');
        setUsername('');
        setUserId('');
        setIp('');
        setStatus(undefined);
        setDateRange([null, null]);
        setPage(1);
    };

    const totalPages = Math.ceil(totalElements / batchSize);

    return (
        <div className="space-y-6">
            {/* Filters */}
            <div className="card p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Фильтры поиска
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Поиск по событию
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Искать по событию"
                                className="input-field pl-10"
                            />
                            <SearchIcon
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"/>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Имя пользователя
                        </label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Имя пользователя"
                            className="input-field"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            ID пользователя
                        </label>
                        <input
                            type="text"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            placeholder="ID пользователя"
                            className="input-field"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            IP-адрес
                        </label>
                        <input
                            type="text"
                            value={ip}
                            onChange={(e) => setIp(e.target.value)}
                            placeholder="IP-адрес"
                            className="input-field"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Статус
                        </label>
                        <select
                            value={status === undefined ? '' : status.toString()}
                            onChange={(e) => setStatus(e.target.value === '' ? undefined : e.target.value === 'true')}
                            className="input-field"
                        >
                            <option value="">Все</option>
                            <option value="true">Успех</option>
                            <option value="false">Ошибка</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Дата
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <input
                                type="datetime-local"
                                lang="ru-RU"
                                value={toDateTimeLocalValue(dateRange[0])}
                                onChange={(e) => setDateRange([e.target.value ? new Date(e.target.value) : null, dateRange[1]])}
                                className="input-field"
                            />
                            <input
                                type="datetime-local"
                                lang="ru-RU"
                                value={toDateTimeLocalValue(dateRange[1])}
                                onChange={(e) => setDateRange([dateRange[0], e.target.value ? new Date(e.target.value) : null])}
                                className="input-field"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center mt-4">
                    <button
                        onClick={clearFilters}
                        className="btn-secondary flex items-center space-x-2"
                    >
                        <XIcon className="w-4 h-4"/>
                        <span>Очистить фильтры</span>
                    </button>

                    <button
                        onClick={handleSearch}
                        className="btn-primary"
                    >
                        Поиск
                    </button>
                </div>
            </div>

            {/* Results */}
            <div className="card p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Результаты поиска
                    </h3>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
            Всего: {totalElements}
          </span>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Timestamp
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                User
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Action
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                IP Address
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Status
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {auditMessages.map((message) => (
                            <tr
                                key={message.id}
                                onClick={() => {
                                    setSelected(message);
                                    setOpened(true);
                                }}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200"
                            >
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {message.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {formatDateTime(message.timestamp)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {message.username}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {message.action}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {message.ipAddress}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        message.success
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {message.success ? 'Успех' : 'Ошибка'}
                    </span>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Показано {((page - 1) * batchSize) + 1} - {Math.min(page * batchSize, totalElements)} из {totalElements}
              </span>
                        </div>

                        <div className="flex items-center space-x-2">
                            <PageSizeSelect
                                table="audit"
                                label="Записей на странице"
                                value={batchSize}
                                onChange={setBatchSize}
                            />

                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="btn-secondary px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Назад
                            </button>

                            <span className="text-sm text-gray-700 dark:text-gray-300">
                {page} из {totalPages}
              </span>

                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages}
                                className="btn-secondary px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Вперед
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Details Drawer */}
            {opened && (
                <div className="fixed inset-0 z-50 overflow-hidden">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setOpened(false)}/>
                    <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white dark:bg-gray-800 shadow-xl">
                        <div
                            className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Детали события
                            </h3>
                            <button
                                onClick={() => setOpened(false)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XIcon className="w-6 h-6"/>
                            </button>
                        </div>

                        {selected && (
                            <div className="p-6 space-y-4">
                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">ID:</span>
                                        <span className="ml-2 text-gray-900 dark:text-gray-100">{selected.id}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Timestamp:</span>
                                        <span className="ml-2 text-gray-900 dark:text-gray-100">
                      {formatDateTime(selected.timestamp)}
                    </span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">User:</span>
                                        <span className="ml-2 text-gray-900 dark:text-gray-100">
                      {selected.username} ({selected.userId})
                    </span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Action:</span>
                                        <span className="ml-2 text-gray-900 dark:text-gray-100">{selected.action}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">IP:</span>
                                        <span
                                            className="ml-2 text-gray-900 dark:text-gray-100">{selected.ipAddress}</span>
                                    </div>
                                    <div>
                                        <span
                                            className="font-medium text-gray-700 dark:text-gray-300">User-Agent:</span>
                                        <span
                                            className="ml-2 text-gray-900 dark:text-gray-100">{selected.userAgent}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Success:</span>
                                        <span
                                            className="ml-2 text-gray-900 dark:text-gray-100">{String(selected.success)}</span>
                                    </div>
                                    <div>
                                        <span
                                            className="font-medium text-gray-700 dark:text-gray-300">Parameters:</span>
                                        <pre
                                            className="mt-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-800 dark:text-gray-200 overflow-x-auto">
                      {selected.parameters}
                    </pre>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Audit;
